import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import {
    FaChartBar,
    FaSpinner,
    FaMoneyBillWave,
    FaUsers,
    FaUserPlus,
    FaDoorOpen,
    FaSmile,
    FaMeh,
    FaFrown,
    FaFilter
} from 'react-icons/fa';
import './BaseFuncionarios.css'; 
import './PeopleAnalytics.css';
import { Bar, Pie } from 'react-chartjs-2'; 
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

// --- FUNÇÕES AUXILIARES DE GRÁFICO (Chart.js) ---
// Use o código abaixo APENAS se você NÃO instalou react-chartjs-2
const AreaChart = ({ areas }) => {
    // ... (Mantém o placeholder se não usar a biblioteca)
    return (
        <div className="chart-container">
             <h4 style={{textAlign: 'center', color: '#101C5D'}}>Distribuição por Área</h4>
             <div className="chart-placeholder">Gráfico de Barras (Requer react-chartjs-2)</div>
        </div>
    );
};
const HumorChart = ({ humor }) => {
    // ... (Mantém o placeholder se não usar a biblioteca)
    return (
        <div className="chart-container">
            <h4 style={{textAlign: 'center', color: '#101C5D'}}>Humor Recente da Equipe</h4>
            <div className="chart-placeholder">Gráfico de Pizza (Requer react-chartjs-2)</div>
        </div>
    );
};

// --- COMPONENTE PRINCIPAL: PeopleAnalytics ---
export default function PeopleAnalytics() {
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // Estados para os Filtros de Período
    const dataAtual = new Date();
    const [filterAno, setFilterAno] = useState(String(dataAtual.getFullYear()));
    const [filterMes, setFilterMes] = useState(String(dataAtual.getMonth() + 1).padStart(2, '0')); // Mês atual

    // Formata moeda
    const formatCurrency = (value) =>
        (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    // Mapeamento de Meses para o SELECT
    const meses = useMemo(() => ([
        { value: '', label: 'Todo o Ano' },
        { value: '01', label: 'Janeiro' }, { value: '02', label: 'Fevereiro' },
        { value: '03', label: 'Março' }, { value: '04', label: 'Abril' },
        { value: '05', label: 'Maio' }, { value: '06', label: 'Junho' },
        { value: '07', label: 'Julho' }, { value: '08', label: 'Agosto' },
        { value: '09', label: 'Setembro' }, { value: '10', label: 'Outubro' },
        { value: '11', label: 'Novembro' }, { value: '12', label: 'Dezembro' }
    ]), []);
    
    // Função para buscar dados da API
    const fetchAnalytics = useCallback(async (ano, mes) => {
        setLoading(true);
        try {
            const params = {
                ...(ano && { ano }), // Inclui 'ano' se não for nulo/vazio
                ...(mes && { mes }), // Inclui 'mes' se não for nulo/vazio
            };
            
            const { data } = await api.get('/api/rh/analytics/visao-geral', { params });
            setAnalyticsData(data);
            
        } catch (error) {
            toast.error("Erro ao carregar dados de People Analytics.");
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Efeito para carregar dados na montagem e quando os filtros mudam
    useEffect(() => {
        fetchAnalytics(filterAno, filterMes === '00' ? null : filterMes); 
        // Se 'mes' for '00' (Todo o Ano), passamos null para a API usar apenas o ano.
    }, [filterAno, filterMes, fetchAnalytics]);

    // Função auxiliar para cor do KPI (Ex: Turnover)
    const getTurnoverColor = (value) => {
        if (value >= 15) return 'kpi-value-bad';
        if (value >= 5) return 'kpi-value-warning';
        return 'kpi-value-good';
    };

    if (loading && !analyticsData) {
        return (
            <div className="rh-container loading-state">
                <h1 className="rh-header"><FaChartBar /> People Analytics</h1>
                <div style={{ textAlign: 'center', marginTop: '50px' }}>
                    <FaSpinner className="icon-spin" size={50} color="#101C5D" />
                    <p style={{ marginTop: '20px', fontSize: '1.2em' }}>Compilando dados...</p>
                </div>
            </div>
        );
    }

    if (!analyticsData) {
        return (
            <div className="rh-container">
                <h1 className="rh-header"><FaChartBar /> People Analytics</h1>
                <p style={{ textAlign: 'center', color: '#dc3545', marginTop: '50px' }}>
                    Não foi possível carregar os dados de People Analytics.
                </p>
            </div>
        );
    }

    const { kpis, dadosDemograficos, dadosEngajamento } = analyticsData;

    // Ajusta o mês exibido para o rótulo
    const mesLabel = filterMes === '00' 
        ? `Ano ${filterAno}` 
        : `Mês de ${meses.find(m => m.value === filterMes)?.label}/${filterAno}`;
    
    const periodoAtual = filterMes === '00' ? filterAno : `${filterMes}/${filterAno}`;

    return (
        <div className="rh-container">
            <h1 className="rh-header"><FaChartBar /> People Analytics</h1>

            {/* Painel de Filtros */}
            <div className="rh-control-panel" style={{ marginBottom: '30px' }}>
                <div className="search-bar filter-group">
                    <FaFilter size={20} color="#101C5D" />
                    <label>Período de Análise:</label>
                    
                    <select 
                        value={filterAno} 
                        onChange={(e) => {
                            setFilterAno(e.target.value);
                            setFilterMes('00'); // Reseta para 'Todo o Ano' ao mudar o ano
                        }}
                    >
                        {/* Gera opções de ano (Ex: ano atual, ano passado) */}
                        <option value={String(dataAtual.getFullYear())}>{dataAtual.getFullYear()}</option>
                        <option value={String(dataAtual.getFullYear() - 1)}>{dataAtual.getFullYear() - 1}</option>
                        {/* Adicione mais lógica de ano conforme necessário */}
                    </select>

                    <select 
                        value={filterMes} 
                        onChange={(e) => setFilterMes(e.target.value)}
                        disabled={loading}
                    >
                        {meses.map(m => (
                            <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                    </select>
                </div>
            </div>


            {/* Painel de KPIs de Visão Geral */}
            <h2 className="analytics-section-title">
                Visão Geral & Métricas Chave <small style={{ color: '#007bff', marginLeft: '10px' }}>({mesLabel})</small>
            </h2>
            <div className="kpi-panel">
                <div className="kpi-card">
                    <FaUsers size={30} color="#101C5D" />
                    <p className="kpi-label">Total de Colaboradores Ativos</p>
                    <p className="kpi-value">
                        {loading ? <FaSpinner className="icon-spin small" /> : kpis.totalColaboradores}
                    </p>
                </div>
                <div className="kpi-card">
                    <FaMoneyBillWave size={30} color="#28a745" />
                    <p className="kpi-label">Salário Médio (Base)</p>
                    <p className="kpi-value">
                        {loading ? <FaSpinner className="icon-spin small" /> : formatCurrency(kpis.salarioMedio)}
                    </p>
                </div>
                <div className="kpi-card">
                    <FaUserPlus size={30} color="#007bff" />
                    <p className="kpi-label">Admissões (Período)</p>
                    <p className="kpi-value">
                        {loading ? <FaSpinner className="icon-spin small" /> : kpis.admissoesPeriodo}
                    </p>
                </div>
                <div className="kpi-card">
                    <FaDoorOpen size={30} color="#dc3545" />
                    <p className="kpi-label">Rescisões (Período)</p>
                    <p className="kpi-value">
                        {loading ? <FaSpinner className="icon-spin small" /> : kpis.rescissoesPeriodo}
                    </p>
                </div>
                <div className="kpi-card">
                    <FaDoorOpen size={30} color="#ffc107" />
                    <p className="kpi-label">Turnover Mensal/Período</p>
                    <p className={`kpi-value ${getTurnoverColor(kpis.turnoverMensal)}`}>
                         {loading ? <FaSpinner className="icon-spin small" /> : `${kpis.turnoverMensal.toFixed(1)}%`}
                    </p>
                </div>
            </div>

            {/* Seção de Gráficos e Insights */}
            <h2 className="analytics-section-title" style={{ marginTop: '30px' }}>Insights Demográficos e de Engajamento</h2>
            
            {loading ? (
                 <div className="chart-loading"><FaSpinner className="icon-spin" size={30} /> Carregando gráficos...</div>
            ) : (
                <div className="charts-container">
                    <AreaChart areas={dadosDemograficos.areas} />
                    <HumorChart humor={dadosEngajamento.humorRecente} />
                </div>
            )}

            {/* Exemplo de Tabela Analítica de Resumo */}
            <h2 className="analytics-section-title" style={{ marginTop: '30px' }}>Resumo de Humor por Categoria (Últimos 30 Dias)</h2>
            <div className="rh-table-container" style={{ maxWidth: '600px', margin: '20px auto' }}>
                <table className="analytics-table">
                    <thead>
                        <tr>
                            <th>Sentimento</th>
                            <th>Ocorrências</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Feliz / Satisfeito</td>
                            <td>{dadosEngajamento.humorRecente.Feliz}</td>
                            <td><FaSmile color="#28a745" /></td>
                        </tr>
                        <tr>
                            <td>Neutro / Indiferente</td>
                            <td>{dadosEngajamento.humorRecente.Neutro}</td>
                            <td><FaMeh color="#ffc107" /></td>
                        </tr>
                        <tr>
                            <td>Triste / Insatisfeito</td>
                            <td>{dadosEngajamento.humorRecente.Triste}</td>
                            <td><FaFrown color="#dc3545" /></td>
                        </tr>
                    </tbody>
                </table>
            </div>

        </div>
    );
}